import { NextSeo } from "next-seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Separator } from "@/components/ui/separator";
import { useRouter } from "next/router";
import { useState } from "react";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import Link from "next/link";
import {
  ShieldCheckIcon,
  LinkIcon,
  ClockIcon,
  GlobeAmericasIcon,
  CheckIcon,
  ArrowRightIcon,
  DocumentCheckIcon,
  BellIcon,
} from "@heroicons/react/24/outline";

export default function LandingPage() {
  const router = useRouter();
  const { account } = useCurrentAccount();
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");

  const handleGetStarted = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !url) return;

    localStorage.setItem("pendingMonitorUrl", url);
    router.push({
      pathname: "/accounts/login",
      query: { email },
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <NextSeo
        title="Uptime Monitoring with PagerDuty, Opsgenie & Slack Integration"
        description="Professional uptime monitoring service with 10-second checks from 16 regions. Native integration with PagerDuty, Opsgenie, Slack, Discord, webhooks. Free plan included."
        canonical="https://uptime-monitor.io/"
        openGraph={{
          title:
            "Uptime Monitoring - Integrated with Your Incident Management Tools",
          description:
            "10-second uptime monitoring from 16 AWS regions. Seamlessly integrates with PagerDuty, Opsgenie, Slack, and your existing incident management workflow.",
          url: "https://uptime-monitor.io/",
        }}
      />

      <Header />

      <main>
        {/* Business Hero */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-muted/50 to-background">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <ShieldCheckIcon className="w-8 h-8 text-primary" />
                <h2 className="text-lg font-semibold text-muted-foreground">
                  Enterprise-Grade Monitoring
                </h2>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Know About Downtime
                <span className="text-primary"> In 10 Seconds</span>
              </h1>

              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                The fastest uptime monitoring available. Distributed checks
                across 16 AWS regions detect issues instantly. Native
                integrations with PagerDuty, Opsgenie, Slack, and your incident
                management tools.
              </p>

              <div className="flex flex-wrap gap-6 mb-10">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 text-primary" />
                  <span className="font-medium">10-second checks</span>
                </div>
                <div className="flex items-center gap-2">
                  <GlobeAmericasIcon className="w-5 h-5 text-primary" />
                  <span className="font-medium">16 AWS regions</span>
                </div>
                <div className="flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-primary" />
                  <span className="font-medium">PagerDuty & Opsgenie</span>
                </div>
              </div>

              {account ? (
                <div className="flex gap-4">
                  <Button
                    size="lg"
                    onClick={() => router.push("/dashboard/monitors")}
                  >
                    Dashboard
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </Button>
                  <Link href="/docs">
                    <Button size="lg" variant="outline">
                      Documentation
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="flex gap-4">
                  <Link href="/accounts/login">
                    <Button size="lg" className="cursor-pointer">
                      Setup Monitoring
                    </Button>
                  </Link>
                  <Link href="/docs">
                    <Button
                      size="lg"
                      variant="outline"
                      className="cursor-pointer"
                    >
                      Documentation
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 border-y">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold">99.999%</div>
                <div className="text-sm text-muted-foreground">
                  <a
                    href="https://status.uptime-monitor.io/6883a5c53de755cc0ebc41e8"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Platform Uptime
                  </a>
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">10s</div>
                <div className="text-sm text-muted-foreground">
                  Detection Speed
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">16</div>
                <div className="text-sm text-muted-foreground">
                  Global Regions
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-sm text-muted-foreground">Monitoring</div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Value Props */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">
                Built for Modern Operations Teams
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We focus on one thing: detecting downtime faster than anyone
                else. Your existing tools handle the rest.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="p-8 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <ClockIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  Fastest Detection Possible
                </h3>
                <p className="text-muted-foreground">
                  Industry-leading 10-second checks, evenly distributed across
                  time. The fastest, most reliable monitoring available.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <LinkIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  Seamless Integration
                </h3>
                <p className="text-muted-foreground mb-4">
                  Works with PagerDuty, Opsgenie, Slack, and your existing
                  incident response tools. We detect, they orchestrate.
                </p>
                <Link
                  href="/docs/notifications"
                  className="text-primary hover:underline text-sm font-medium"
                >
                  View all integrations →
                </Link>
              </Card>

              <Card className="p-8 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <ShieldCheckIcon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  No False Positives
                </h3>
                <p className="text-muted-foreground mb-4">
                  Learn how to eliminate false positives and get the most from
                  your monitoring with proven strategies.
                </p>
                <Link
                  href="/docs/best-practices"
                  className="text-primary hover:underline text-sm font-medium"
                >
                  Read best practices →
                </Link>
              </Card>
            </div>
          </div>
        </section>

        <Separator />

        {/* Incident Management Integration */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge variant="outline" className="mb-6">
                  <BellIcon className="w-3 h-3 mr-1" />
                  Incident Response
                </Badge>

                <h2 className="text-3xl font-bold mb-4">
                  Your Tools, Our Detection
                </h2>

                <p className="text-lg text-muted-foreground mb-6">
                  We believe in doing one thing exceptionally well. Our
                  monitoring detects issues faster than any other service. Your
                  incident management platform takes it from there.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">Automatic Incident Creation</p>
                      <p className="text-sm text-muted-foreground">
                        Issues flow directly into PagerDuty or Opsgenie with
                        full context
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">
                        Respect Your Escalation Policies
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Your on-call schedules and routing rules remain in your
                        control
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">Rich Context & Runbooks</p>
                      <p className="text-sm text-muted-foreground">
                        Include response times, affected regions, and custom
                        metadata
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Link href="/docs/notifications/pagerduty">
                    <Button className="cursor-pointer">
                      PagerDuty Setup Guide
                    </Button>
                  </Link>
                  <Link href="/docs/notifications/opsgenie">
                    <Button variant="outline" className="cursor-pointer">
                      Opsgenie Setup Guide
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="space-y-4">
                <Card className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
                    <h4 className="font-semibold">Alert Flow Example</h4>
                  </div>

                  <div className="space-y-3 font-mono text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:00</span>
                      <span>Check from us-east-2: all good</span>
                    </div>
                    <div className="flex items-center gap-3 text-red-600">
                      <span className="text-muted-foreground">00:06</span>
                      <span className="font-semibold">Server crashes</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:10</span>
                      <span>Check from us-east-1: error</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:20</span>
                      <span>Check from eu-west-1: error</span>
                    </div>
                    <div className="flex items-center gap-3 text-red-600">
                      <span className="text-muted-foreground">00:20</span>
                      <span className="font-semibold">Alert triggered</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:20</span>
                      <span>Status page updated automatically</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:21</span>
                      <span>PagerDuty incident #1234 created</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">00:21</span>
                      <span>
                        On-call engineer paged{" "}
                        <span className="font-semibold">in ~15 seconds</span>{" "}
                        after crash
                      </span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-muted/50">
                  <h4 className="font-semibold mb-3">Supported Integrations</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      {
                        name: "PagerDuty",
                        href: "/docs/notifications/pagerduty",
                      },
                      {
                        name: "Opsgenie",
                        href: "/docs/notifications/opsgenie",
                      },
                      { name: "Slack", href: "/docs/notifications/slack" },
                      { name: "Discord", href: "/docs/notifications/discord" },
                      {
                        name: "incident.io",
                        href: "/docs/notifications/incident-io",
                      },
                      {
                        name: "Webhooks",
                        href: "/docs/notifications/webhooks",
                      },
                      { name: "Email", href: "/docs/notifications/email" },
                      { name: "SMS", href: "/docs/notifications/sms" },
                    ].map((integration) => (
                      <Link
                        key={integration.name}
                        href={integration.href}
                        className="inline-flex"
                      >
                        <Badge
                          variant="secondary"
                          className="hover:bg-secondary/80 cursor-pointer transition-colors"
                        >
                          {integration.name}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Status Pages Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4">
                <DocumentCheckIcon className="w-3 h-3 mr-1" />
                Included Free
              </Badge>
              <h2 className="text-3xl font-bold mb-4">
                Professional Status Pages
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Keep your customers informed with public status pages on your
                own domain.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              <Card className="p-6">
                <h3 className="font-semibold mb-2">Your Domain</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Host at status.yourcompany.com with automatic SSL
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Real-time Updates</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Automatically reflects the current status of all monitors
                </p>
                <div className="flex gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-600"></div>
                  <div className="w-2 h-2 rounded-full bg-green-600"></div>
                  <div className="w-2 h-2 rounded-full bg-red-600"></div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Incident Management</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Post updates, track progress, maintain incident history
                </p>
                <Badge variant="outline" className="text-xs">
                  90-day retention
                </Badge>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Public Visibility</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Share your service status transparently with customers
                </p>
                <Badge variant="outline" className="text-xs">
                  Always accessible
                </Badge>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Password Protection</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Optional basic auth for internal status pages
                </p>
                <Badge variant="secondary" className="text-xs">
                  Pro feature
                </Badge>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-2">Multiple Pages</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create separate status pages for different services
                </p>
                <Badge variant="default" className="text-xs">
                  Unlimited pages
                </Badge>
              </Card>
            </div>

            <div className="text-center">
              <Link
                href="https://status.uptime-monitor.io/6883a5c53de755cc0ebc41e8"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" variant="outline">
                  View Live Demo
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <Separator />

        {/* Pricing */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Transparent Pricing</h2>
              <p className="text-lg text-muted-foreground">
                Start monitoring in seconds. No credit card required.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
              <Card className="p-8">
                <h3 className="text-xl font-semibold mb-2">Free</h3>
                <div className="text-3xl font-bold mb-1">$0</div>
                <p className="text-sm text-muted-foreground mb-6">
                  Perfect for small projects
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    10 monitors (up to 3 locations each)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    30-second checks
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    HTTPS, TCP, ping checks support
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    10 free daily alert emails
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Basic status pages
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Basic integration alerts (Slack, webhooks, PagerDuty, etc.)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    90 days data retention
                  </li>
                </ul>

                <Link href="/accounts/login">
                  <Button className="w-full cursor-pointer" variant="outline">
                    Start Free
                  </Button>
                </Link>
              </Card>

              <Card className="p-8 border-primary shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-semibold">Pro</h3>
                  <Badge>Most Popular</Badge>
                </div>
                <div className="text-3xl font-bold mb-1">
                  From $4.95
                  <span className="text-lg font-normal text-muted-foreground">
                    /mo
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  For professional teams
                </p>

                <ul className="space-y-3 mb-8">
                  {/* <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    10 monitors (up to 16 locations each)
                  </li> */}
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Up to 16 locations for each monitor
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    10-second checks
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Advanced HTTP(S) monitoring
                  </li>
                  {/* <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    10 free daily alert emails
                  </li> */}
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Unlimited integration alerts (Slack, webhooks, PagerDuty,
                    etc.)
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Status pages on a custom domain
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Optional password protection
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    API Access for programmatic control
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    SSL certificate monitoring
                  </li>
                </ul>

                <Link href="/accounts/login">
                  <Button className="w-full cursor-pointer">
                    Setup Monitoring
                  </Button>
                </Link>
              </Card>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Need more monitors or custom requirements?
              </p>
              <a href="mailto:hi@uptime-monitor.io?subject=Enterprise%20Pricing%20Inquiry">
                <Button variant="link">
                  Contact us for Enterprise pricing
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
